import { makeObservable, observable, flow, computed } from "mobx";
import { AzureBlobStore, NotificationStoreInstance } from "@common/stores";
import { BlobEntityType, MimeType } from "@common/models/Enums";
import { UserStore } from '@hospital/views/shared/userprofile';
import { v4 as uuid } from 'uuid';
import { IPhotoRequest, IBlobSasModel } from "@common/models";
import { VeterinarianService } from "@common/services";

export interface ISignatureStoreInit {
    userStore: UserStore
}

export interface ISignatureStore {
    userStore: UserStore | undefined,
    signatureIsBeingUpdated: boolean;
    hasSignature: boolean;
    signatureUrl: string | undefined;
    signatureName: string | undefined;
    saveVetSignature(this: ISignatureStore, image: string): any;
    deleteVetSignature(this: ISignatureStore): any;
}


export class SignatureStore implements ISignatureStore {
    userStore!: UserStore;
    @observable signatureIsBeingUpdated: boolean = false;

    constructor(init: ISignatureStoreInit) {
        Object.assign(this, init);
        makeObservable(this, undefined, {
            autoBind: true
        }); //use decorators    
    }

    @computed get hasSignature() {
        return this.userStore.vet?.activeSignature != null;
    }

    @computed get signatureUrl() {
        return this.userStore?.vet?.activeSignature?.signature?.photoUrl;
    }

    @computed get signatureName() {
        return this.userStore?.vet?.activeSignature?.signature?.fileName;
    }

    saveVetSignature = flow(function* (this: SignatureStore, imageUrl: string): Generator<Promise<any>, void, any> {
        this.signatureIsBeingUpdated = true;

        const blobStore = new AzureBlobStore({ containerName: BlobEntityType.Signatures });
        const filename = `signaturePhoto-${uuid()}.png`;
        const blobSas: IBlobSasModel = yield blobStore.uploadDataUrl(imageUrl, filename, MimeType.PNG);

        if (blobSas) {
            const photoRequest: IPhotoRequest = {
                container: blobSas.container,
                fileName: blobSas.fileName,
                photoUrl: blobSas.sasUrl
            };

            yield VeterinarianService.saveVeterinarianSignature(this.userStore.vet.veterinarianId, photoRequest)
                .then(NotificationStoreInstance.onSuccess("Signature Added"))
                .catch(NotificationStoreInstance.onError("Failed to add signature", null));

            this.signatureIsBeingUpdated = false;
            yield this.userStore.refreshUser(this.userStore.user.userId);

        }
        else {
            throw "There was a failure saving signature to azure blog storage";
        }
    });

    deleteVetSignature = flow(function* (this: SignatureStore): Generator<Promise<any>, void, any> {
        this.signatureIsBeingUpdated = true;

        yield VeterinarianService.deleteVeterinarianSignature(this.userStore.vet.veterinarianId)
            .then(NotificationStoreInstance.onSuccess("Signature Deleted"))
            .catch(NotificationStoreInstance.onError(`Failed to delete signature`, null));

        this.signatureIsBeingUpdated = false;

        yield this.userStore.refreshUser(this.userStore.user.userId);
    });

}
